import { API_URL, IS_CONFIGURED } from '@/amplify-config'
import { toast } from '@/components/ui/Toaster'

const MOCK_MODE = !IS_CONFIGURED

async function authHeader(): Promise<Record<string, string>> {
  if (MOCK_MODE) return {}
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth')
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message)
  }
}

// The shared daily AI budget (backend utils/ai_budget.py): once today's cap
// across Cassidy, BD and Penser+ is spent, AI routes answer 429
// {code: 'ai_budget_exhausted', error: <message>}, AI jobs fail with the
// message, and the chat replies with it. Show that message, not a generic one.
const AI_BUDGET_PREFIX = "Today's AI budget of "

export function isAiBudgetMessage(text: unknown): text is string {
  return typeof text === 'string' && text.startsWith(AI_BUDGET_PREFIX)
}

/** The budget message carried by an API error, a job error or a thrown
 *  Error, or null when the failure is something else. */
export function aiBudgetMessage(err: unknown): string | null {
  if (isAiBudgetMessage(err)) return err
  if (err instanceof ApiError && err.status === 429 && typeof err.body === 'string') {
    try {
      const parsed = JSON.parse(err.body) as { code?: string; error?: string }
      if (parsed.code === 'ai_budget_exhausted' && parsed.error) return parsed.error
    } catch { /* not JSON */ }
    return null
  }
  if (err instanceof Error && isAiBudgetMessage(err.message)) return err.message
  return null
}

// Prevent multiple "database waking up" toasts when several requests fire at once.
let _dbResumingToastShown = false

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (MOCK_MODE) {
    const { mockApi } = await import('./mock-api')
    return mockApi<T>(method, path, body)
  }
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
  }
  const url = `${API_URL.replace(/\/$/, '')}${path}`
  const serializedBody = body ? JSON.stringify(body) : undefined

  // Retry up to 7 times (35 s total at 5 s each) when Aurora is resuming from zero.
  for (let attempt = 0; attempt <= 6; attempt++) {
    const res = await fetch(url, { method, headers, body: serializedBody })

    if (res.status === 503) {
      const errText = await res.text().catch(() => '')
      let errObj: { error?: string; message?: string } = {}
      try { errObj = JSON.parse(errText) } catch { /* not JSON */ }

      if (errObj.error === 'database_resuming' && attempt < 6) {
        if (!_dbResumingToastShown) {
          _dbResumingToastShown = true
          toast.info(
            'Waking up from off-peak standby',
            'First use outside business hours takes ~30 seconds. Keep this tab open — it continues automatically.',
            { duration: 35_000 },
          )
          setTimeout(() => { _dbResumingToastShown = false }, 60_000)
        }
        await new Promise<void>((r) => setTimeout(r, 5_000))
        continue
      }
      throw new ApiError(503, errObj, `${method} ${path} → 503`)
    }

    if (!res.ok) {
      const errBody = await res.text().catch(() => '')
      throw new ApiError(res.status, errBody, `${method} ${path} → ${res.status}`)
    }
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  throw new ApiError(503, { error: 'database_resuming' }, `${method} ${path} → database did not recover in time`)
}

/**
 * Quietly probe whether the database is awake, used at bootstrap to hold the
 * loading screen through an Aurora scale-to-zero resume. Overnight (MinCapacity
 * drops to 0 at 20:00 PT) the first request wakes the cluster, which takes
 * ~15–45 s; without this gate the app shell renders against a paused DB and
 * every page flashes errors. By blocking the loader on this probe, a late-night
 * first login just looks like a slightly slow sign-in.
 *
 * Differs from request()'s per-call retry: no toast (the spinner already says
 * "loading"), and more patient — at bootstrap we'd rather wait than fail.
 * Resolves true once the DB answers; resolves false if it never comes back in
 * the window, in which case the caller should let the app render anyway so its
 * own calls surface real errors / their own retries (never trap the user on a
 * spinner forever).
 */
export async function probeDbReady(maxWaitMs = 90_000): Promise<boolean> {
  if (MOCK_MODE) return true
  const url = `${API_URL.replace(/\/$/, '')}/verticals` // cheapest universal authed GET
  const deadline = Date.now() + maxWaitMs
  for (;;) {
    try {
      const res = await fetch(url, { method: 'GET', headers: await authHeader() })
      if (res.status === 503) {
        let errObj: { error?: string } = {}
        try { errObj = JSON.parse(await res.text()) } catch { /* not JSON */ }
        if (errObj.error === 'database_resuming' && Date.now() < deadline) {
          await new Promise<void>((r) => setTimeout(r, 4_000))
          continue
        }
        return false
      }
      // Any non-503 (200/401/403/even 500) means the DB/edge answered — readiness
      // is about the cluster being awake, not this call's authz. Let the app render.
      return true
    } catch {
      if (Date.now() < deadline) {
        await new Promise<void>((r) => setTimeout(r, 4_000))
        continue
      }
      return false
    }
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
}

/** Fetch private HTML through the same authenticated boundary as JSON requests. */
export async function getAuthenticatedText(path: string): Promise<string> {
  if (MOCK_MODE) throw new Error('Connect to the portal to open stored reports.')
  const response = await fetch(`${API_URL.replace(/\/$/, '')}${path}`, { headers: await authHeader(), cache: 'no-store' })
  if (!response.ok) throw new ApiError(response.status, null, 'Could not load the report.')
  return response.text()
}
